import React, { useState, useEffect, ReactNode, useRef } from 'react'

interface TypewriterTextProps {
    children: ReactNode
    speed?: number
    startDelay?: number
    onComplete?: () => void
    className?: string
}

const extractWords = (node: ReactNode): string[] => {
    const allWords: string[] = []
    extractWordsRecursive(node, allWords)
    return allWords
}

const extractWordsRecursive = (node: ReactNode, wordArray: string[]): void => {
    if (typeof node === 'string') {
        const words = node.split(/(\s+)/)
        for (const part of words) {
            if (!/\s+/.test(part) && part.length > 0) {
                wordArray.push(part)
            }
        }
    } else if (typeof node === 'number') {
        wordArray.push(node.toString())
    } else if (React.isValidElement(node)) {
        extractWordsRecursive(node.props.children, wordArray)
    } else if (Array.isArray(node)) {
        for (const child of node) {
            extractWordsRecursive(child, wordArray)
        }
    }
}

const renderPartialContent = (node: ReactNode, targetWords: number, currentPos = { wordCount: 0 }): ReactNode => {
    if (currentPos.wordCount >= targetWords) return null
    
    if (typeof node === 'string') {
        const words = node.split(/(\s+)/) 
        const elements = []
        
        for (const part of words) {
            if (currentPos.wordCount >= targetWords) break
            
            if (/\s+/.test(part)) {
                if (currentPos.wordCount > 0) {
                    elements.push(part)
                }
            } else if (part.length > 0) {
              
                if (currentPos.wordCount < targetWords) {
                    const isNewWord = currentPos.wordCount === targetWords - 1
                                         if (isNewWord) {
                         elements.push(
                             React.createElement('span', {
                                 key: `word-${currentPos.wordCount}`,
                                 className: 'typewriter-word-new',
                                 style: { display: 'inline' }
                             }, part)
                         )
                     } else {
                         elements.push(part)
                     }
                    currentPos.wordCount++
                }
            }
        }
        
        return elements.length > 0 ? elements : null
    }
    
    if (typeof node === 'number') {
        const str = node.toString()
        if (currentPos.wordCount < targetWords) {
            currentPos.wordCount++
            return str
        }
        return null
    }
    
    if (React.isValidElement(node)) {
        const childrenResult = renderPartialContent(node.props.children, targetWords, currentPos)
        if (childrenResult === null || childrenResult === '') return null
        
        return React.cloneElement(node, { key: Math.random() }, childrenResult)
    }
    
    if (Array.isArray(node)) {
        const results = []
        for (const child of node) {
            if (currentPos.wordCount >= targetWords) break
            const result = renderPartialContent(child, targetWords, currentPos)
            if (result !== null && result !== '') {
                results.push(result)
            }
        }
        return results.length > 0 ? results : null
    }
    
    return null
}

const TypewriterText: React.FC<TypewriterTextProps> = ({
    children,
    speed = 20, 
    startDelay = 0,
    onComplete,
    className = ''
}) => {
    const [currentWordCount, setCurrentWordCount] = useState(0)
    const [isComplete, setIsComplete] = useState(false)
    const [hasStarted, setHasStarted] = useState(false)
    const [contentHeight, setContentHeight] = useState<number | null>(null)
    
    const measureRef = useRef<HTMLDivElement>(null)
    const allWords = extractWords(children)

  
    useEffect(() => {
        if (measureRef.current) {
            setContentHeight(measureRef.current.offsetHeight)
        }
    }, [children])

    useEffect(() => {
        const startTimer = setTimeout(() => {
            setHasStarted(true)
        }, startDelay)

        return () => clearTimeout(startTimer)
    }, [startDelay])

    useEffect(() => {
        if (!hasStarted || isComplete || currentWordCount >= allWords.length) {
            if (currentWordCount >= allWords.length && !isComplete) {
                setIsComplete(true)
                onComplete?.()
            }
            return
        }

       
        const currentSpeed = Math.max(1, speed + (Math.random() * 10 - 5)) 

        const timer = setTimeout(() => {
            setCurrentWordCount(prev => prev + 1)
        }, currentSpeed)

        return () => clearTimeout(timer)
    }, [currentWordCount, allWords.length, speed, hasStarted, isComplete, onComplete, allWords])

    const partialContent = hasStarted ? renderPartialContent(children, currentWordCount) : null


    useEffect(() => {
   
        const timer = setTimeout(() => {
            const newWordSpans = document.querySelectorAll('.typewriter-word-new')
            newWordSpans.forEach((span) => {
               
                void (span as HTMLElement).offsetHeight 
                span.classList.remove('typewriter-word-new')
                span.classList.add('typewriter-word-visible')
            })
        }, 10) 

        return () => clearTimeout(timer)
    }, [currentWordCount])

  
    useEffect(() => {
        if (isComplete) {
            const timer = setTimeout(() => {
                const newWordSpans = document.querySelectorAll('.typewriter-word-new')
                newWordSpans.forEach((span) => {
                    void (span as HTMLElement).offsetHeight
                    span.classList.remove('typewriter-word-new')
                    span.classList.add('typewriter-word-visible')
                })
            }, 10)

            return () => clearTimeout(timer)
        }
    }, [isComplete])

    return (
        <>
            {/* Hidden div to measure full content height */}
            <div 
                ref={measureRef}
                style={{ 
                    position: 'absolute', 
                    visibility: 'hidden', 
                    top: '-9999px',
                    width: '100%'
                }}
                className={`typewriter-content ${className}`}
            >
                {children}
            </div>
            
            {/* Actual content with reserved height */}
            <div 
                className={`typewriter-content ${className}`}
                style={{ 
                    minHeight: contentHeight ? `${contentHeight}px` : 'auto'
                }}
            >
                {partialContent}
            </div>
        </>
    )
}

export default TypewriterText 